import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Copy, FilePlus2, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

import { formatPriceCents } from "@/components/services/service-meta";
import { FichaTecnicaLinha, type LinhaDaFicha } from "./FichaTecnicaLinha";
import { FichaTecnicaSeletor } from "./FichaTecnicaSeletor";
import { FichaTecnicaEquipamentos } from "./FichaTecnicaEquipamentos";
import { MaterialLineDialog } from "@/components/estoque/MaterialLineDialog";
import { agruparMateriais, rotuloDoMaterial } from "@/lib/agrupar-materiais";

const NOVA_CATEGORIA = "cat:";
const NOVO_INSUMO = "__novo_insumo__";
const paraNumero = (v: string) => Number((v || "0").replace(",", "."));

/**
 * Ficha técnica do par serviço × material, dentro do próprio diálogo do serviço.
 *
 * Fica aqui, e não num módulo à parte, porque ficha técnica é **definição do serviço**: quem cria
 * "Coroa Emax" sabe naquele momento o que ela consome.
 *
 * O par tem VÁRIAS fichas: na planilha do Jun, "Coroa impressa (BioCrown)" e "(BioCrown Diamond)"
 * são o mesmo serviço e o mesmo material, com receitas e custos diferentes. Por isso cada ficha
 * tem nome e o seletor no topo troca entre elas.
 */
export function FichaTecnicaTab({
  serviceCategoryId,
  serviceLabel,
  materialOptionIds,
  fichaInicial,
}: {
  serviceCategoryId: string;
  /** O serviço sendo editado — o diálogo é genérico, e sem o nome aqui se perde o contexto. */
  serviceLabel: string;
  /** Materiais do cardápio que o serviço aceita — a receita muda com o material. */
  materialOptionIds: string[];
  /** Quem chegou clicando numa ficha da lista de serviços já disse qual quer ver. */
  fichaInicial?: { id: string; materialOptionId: string | null };
}) {
  const utils = trpc.useUtils();
  const [materialOptionId, setMaterialOptionId] = useState<string | null>(
    fichaInicial?.materialOptionId ?? null,
  );
  const [fichaId, setFichaId] = useState<string | null>(fichaInicial?.id ?? null);
  const [nome, setNome] = useState("");
  const [linhas, setLinhas] = useState<LinhaDaFicha[]>([]);
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  /** O cadastro de insumo mora no Estoque desde 30/09. Sem este atalho, quem monta a receita e
   *  descobre que falta um material teria que largar a ficha pela metade pra ir cadastrar. */
  const [criandoInsumo, setCriandoInsumo] = useState(false);
  const [sujo, setSujo] = useState(false);

  const materiaisQuery = trpc.prosthesis.catalog.myMaterialLines.useQuery();
  // Duas listas de propósito: a CHAVE da ficha é o material do cardápio (o que o dentista pediu),
  // e a linha em aberto é química (de onde saem os candidatos na bancada).
  const ofertasQuery = trpc.prosthesis.catalog.materialOptions.useQuery();
  const equipamentosQuery = trpc.equipamento.listar.useQuery();
  const categoriasQuery = trpc.prosthesis.catalog.materialCategories.useQuery();
  const listaQuery = trpc.fichaTecnica.listar.useQuery(
    { serviceCategoryId, materialOptionId },
    { enabled: !!serviceCategoryId },
  );

  // Só material ativo: pôr na receita um material que o laboratório desligou seria montar custo em
  // cima de algo que não se compra mais.
  const materiais = useMemo(
    () => (materiaisQuery.data ?? []).filter((m: any) => m.active !== false),
    [materiaisQuery.data],
  );
  const categorias = useMemo(() => categoriasQuery.data ?? [], [categoriasQuery.data]);

  /**
   * A lista de escolha vem agrupada pelo `subcategory` — que na tela se chama CATEGORIA (Brocas,
   * Resinas, Embalagem) e é como o laboratório pensa o estoque. Sem ela, cai no `materialCategory`
   * (a química, que a tela chama de subcategoria) — assim nenhum material fica num balde sem nome.
   */
  const gruposDeMaterial = useMemo(() => agruparMateriais(materiais as any[]), [materiais]);
  const doServico = (ofertasQuery.data ?? []).filter((o: any) => materialOptionIds.includes(o.id));
  const fichas = useMemo(() => listaQuery.data?.fichas ?? [], [listaQuery.data]);

  const paraLinhas = (f: any): LinhaDaFicha[] =>
    (f?.lines ?? []).map((l: any) => ({
      materialLineId: l.materialLineId,
      materialCategoryId: l.materialCategoryId,
      rotulo: l.materialLine
        ? `${l.materialLine.name}${l.materialLine.manufacturerName ? ` (${l.materialLine.manufacturerName})` : ""}`
        : `Qualquer ${l.materialCategory?.label ?? "insumo"}`,
      unidade: l.materialLine?.unit ?? null,
      precoCents: l.materialLine?.purchasePriceCents ?? null,
      indireto: l.indireto,
      quantidade: String(l.quantityNet).replace(".", ","),
      fator: String(l.correctionFactor).replace(".", ","),
    }));

  // Escolhe a ficha a mostrar. `sujo` protege o que foi digitado de um refetch em segundo plano.
  useEffect(() => {
    if (!listaQuery.data || sujo) return;
    const alvo = fichas.find((f: any) => f.id === fichaId) ?? fichas[0] ?? null;
    setFichaId(alvo?.id ?? null);
    setNome(alvo?.name ?? "");
    setLinhas(paraLinhas(alvo));
    setEquipmentIds((alvo?.equipamentos ?? []).map((e: any) => e.id));
  }, [listaQuery.data, sujo]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Largar o que está aberto avisa quando há alteração pendente — trocar de ficha é o gesto que
   *  mais perde trabalho aqui, e o aviso de "não salvas" sozinho não segura ninguém. */
  function podeLargar() {
    return !sujo || window.confirm("Você tem alterações não salvas nesta ficha. Descartar?");
  }

  function trocarFicha(valor: string) {
    if (valor === fichaId || !podeLargar()) return;
    setSujo(false);
    const f = fichas.find((x: any) => x.id === valor);
    setFichaId(f?.id ?? null);
    setNome(f?.name ?? "");
    setLinhas(paraLinhas(f));
    setEquipmentIds((f?.equipamentos ?? []).map((e: any) => e.id));
  }

  function novaFicha() {
    if (!podeLargar()) return;
    setSujo(false);
    setFichaId(null);
    setNome("");
    setLinhas([]);
    setEquipmentIds([]);
  }

  /** O que vai pro servidor — o mesmo corpo em salvar e em duplicar, pra não haver duas versões
   *  da mesma conversão de linha. */
  const corpoDaFicha = (name: string) => ({
    serviceCategoryId,
    materialOptionId,
    equipmentIds,
    name,
    lines: linhas.map((l) => ({
      materialLineId: l.materialLineId,
      materialCategoryId: l.materialCategoryId,
      quantityNet: l.quantidade,
      correctionFactor: l.fator || "1",
    })),
  });

  /**
   * Copia a ficha aberta com outro nome.
   *
   * Duplica o que está NA TELA, não o que está salvo: quem ajustou uma quantidade e percebeu que
   * queria uma variação disso perderia o ajuste se a cópia viesse do banco. Em troca, a ficha
   * original volta pro que estava salvo — o aviso diz isso antes.
   */
  function duplicar() {
    const aviso = sujo
      ? "As alterações não salvas vão para a cópia, e a ficha atual volta ao que estava salvo.\n\n"
      : "";
    const escolhido = window.prompt(`${aviso}Nome da nova ficha:`, `${nome} (cópia)`);
    if (escolhido === null) return;
    const limpo = escolhido.trim();
    if (limpo.length < 2) {
      toast.error("Dê um nome com pelo menos 2 letras.");
      return;
    }
    duplicando.current = true;
    salvar.mutate(corpoDaFicha(limpo));
  }

  /** Duplicar usa a MESMA mutation de salvar, só sem `id`. O que muda é o aviso na tela, e é
   *  só pra isso que esta marca existe. */
  const duplicando = useRef(false);

  const salvar = trpc.fichaTecnica.salvar.useMutation({
    onSuccess: (r) => {
      toast.success(duplicando.current ? "Ficha duplicada." : "Ficha técnica salva.");
      duplicando.current = false;
      setSujo(false);
      setFichaId(r.id);
      utils.fichaTecnica.listar.invalidate();
      utils.fichaTecnica.resumo.invalidate();
    },
    onError: (e) => {
      duplicando.current = false;
      toast.error(e.message || "Erro ao salvar a ficha.");
    },
  });

  const remover = trpc.fichaTecnica.remover.useMutation({
    onSuccess: () => {
      toast.success("Ficha removida.");
      setSujo(false);
      setFichaId(null);
      setNome("");
      setLinhas([]);
      utils.fichaTecnica.listar.invalidate();
      utils.fichaTecnica.resumo.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao remover a ficha."),
  });

  function adicionar(valor: string) {
    if (valor === NOVO_INSUMO) {
      setCriandoInsumo(true);
      return;
    }
    if (!valor) return;
    setSujo(true);
    if (valor.startsWith(NOVA_CATEGORIA)) {
      const id = valor.slice(NOVA_CATEGORIA.length);
      const cat = categorias.find((c) => c.id === id);
      setLinhas((v) => [
        ...v,
        { materialLineId: null, materialCategoryId: id, rotulo: `Qualquer ${cat?.label ?? "insumo"}`, unidade: null, precoCents: null, indireto: false, quantidade: "1", fator: "1" },
      ]);
      return;
    }
    const m = materiais.find((x) => x.id === valor);
    if (!m) return;
    setLinhas((v) => [
      ...v,
      {
        materialLineId: m.id,
        materialCategoryId: null,
        rotulo: `${m.name} (${m.manufacturer.name})`,
        unidade: m.unit,
        precoCents: m.purchasePriceCents,
        indireto: m.indirectConsumption,
        quantidade: "1",
        // O fator do material é só o ponto de partida: a ficha pode ter perda própria.
        fator: m.defaultCorrectionFactor != null ? String(m.defaultCorrectionFactor).replace(".", ",") : "1",
      },
    ]);
  }

  /**
   * O que as máquinas marcadas somam, no material desta ficha.
   *
   * O valor vem pronto do servidor (`custoPorMaterial`) em vez de ser recalculado aqui: a regra de
   * qual gasto vale pra qual material — o específico substitui o geral — mora num lugar só.
   * A chave cai em `__padrao__` quando a máquina não declara este material: é o gasto de escape.
   */
  const daMaquina = useMemo(() => {
    const porId = new Map((equipamentosQuery.data ?? []).map((e: any) => [e.id, e]));
    const chave = materialOptionId ?? "__padrao__";
    let total = 0;
    let semPreco = 0;
    for (const id of equipmentIds) {
      const e: any = porId.get(id);
      if (!e) continue;
      total += e.custoPorMaterial?.[chave] ?? e.custoPorMaterial?.__padrao__ ?? 0;
      semPreco += e.semPrecoPorMaterial?.[chave] ?? e.semPrecoPorMaterial?.__padrao__ ?? 0;
    }
    return { total, semPreco };
  }, [equipmentIds, equipamentosQuery.data, materialOptionId]);

  // Calculado na tela pra responder enquanto se digita; o servidor recalcula ao salvar.
  const totais = useMemo(() => {
    let total = 0;
    let temAberto = false;
    let semPreco = 0;
    for (const l of linhas) {
      if (l.materialCategoryId) {
        temAberto = true;
        continue;
      }
      if (l.indireto) continue;
      if (l.precoCents == null) {
        semPreco++;
        continue;
      }
      total += paraNumero(l.quantidade) * (paraNumero(l.fator) || 1) * l.precoCents;
    }
    // A máquina entra no mesmo total das linhas: pra quem lê, o custo do trabalho é um número só.
    return {
      totalCents: Math.round(total + daMaquina.total),
      temAberto,
      semPreco: semPreco + daMaquina.semPreco,
    };
  }, [linhas, daMaquina]);

  // Ficha vazia não tem margem: "custo R$ 0,00, margem R$ 999,00" leria como margem de 100% quando
  // na verdade ninguém ainda disse o que a peça consome. Ficha só com máquina já tem custo real.
  const temConteudo = linhas.length > 0 || equipmentIds.length > 0;
  const precoVenda = temConteudo ? (listaQuery.data?.precoVendaCents ?? null) : null;
  const margem = precoVenda != null && !totais.temAberto ? precoVenda - totais.totalCents : null;
  const podeSalvar = nome.trim().length >= 2 && sujo;

  if (listaQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-muted/40">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Ficha técnica de</p>
        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{serviceLabel}</p>
      </div>

      {doServico.length > 1 && (
        <div>
          <p className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">Material</p>
          <select
            value={materialOptionId ?? ""}
            onChange={(e) => {
              if (!podeLargar()) return;
              setSujo(false);
              setFichaId(null);
              setMaterialOptionId(e.target.value || null);
            }}
            className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm dark:border-border dark:bg-card dark:text-foreground"
          >
            <option value="">Padrão (qualquer material)</option>
            {doServico.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <FichaTecnicaSeletor fichas={fichas as any} fichaId={fichaId} onEscolher={trocarFicha} />

      <FichaTecnicaEquipamentos
        disponiveis={(equipamentosQuery.data ?? []) as any}
        escolhidos={equipmentIds}
        materialOptionId={materialOptionId}
        naFicha={((fichas.find((f: any) => f.id === fichaId)?.equipamentos ?? []) as any) ?? []}
        onEscolher={(ids) => {
          setSujo(true);
          setEquipmentIds(ids);
        }}
      />

      <div>
        <p className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">Nome desta ficha</p>
        <input
          value={nome}
          onChange={(e) => {
            setSujo(true);
            setNome(e.target.value);
          }}
          placeholder="Ex: MC XL, BioCrown, BioCrown Diamond"
          className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
        />
        <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
          O mesmo serviço e material podem ter mais de uma receita — o nome é o que distingue uma da
          outra na hora de produzir.
        </p>
      </div>

      {/* `table-fixed` porque o nome do material é longo e, no layout automático, ele roubava a
          largura dos campos de quantidade — que são justamente onde se digita "0,0011". */}
      <table className="w-full table-fixed text-xs">
        <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
          <tr>
            <th className="pb-1.5 text-left font-semibold">Insumo</th>
            <th className="w-[4.5rem] pb-1.5 text-right font-semibold">Qtd.</th>
            <th className="w-14 pb-1.5 text-right font-semibold">Fator</th>
            <th className="w-16 pb-1.5 text-right font-semibold">Bruta</th>
            <th className="w-20 pb-1.5 text-right font-semibold">Custo</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <FichaTecnicaLinha
              key={`${l.materialLineId ?? l.materialCategoryId}-${i}`}
              linha={l}
              onChange={(nova) => {
                setSujo(true);
                setLinhas((v) => v.map((x, j) => (j === i ? nova : x)));
              }}
              onRemover={() => {
                setSujo(true);
                setLinhas((v) => v.filter((_, j) => j !== i));
              }}
            />
          ))}
          {linhas.length === 0 && (
            <tr>
              <td colSpan={6} className="py-4 text-center text-[11.5px] text-zinc-400 dark:text-muted-foreground">
                Nenhum insumo nesta ficha. Adicione o que esse serviço consome.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <select
        value=""
        onChange={(e) => adicionar(e.target.value)}
        aria-label="Adicionar insumo à ficha"
        className="h-8 w-full rounded-md border border-dashed border-zinc-300 bg-white px-2 text-xs text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground"
      >
        <option value="">+ Adicionar insumo</option>
        <option value={NOVO_INSUMO}>+ Cadastrar um insumo novo…</option>
        {gruposDeMaterial.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.itens.map((m) => (
              <option key={m.id} value={m.id}>
                {rotuloDoMaterial(m)}
              </option>
            ))}
          </optgroup>
        ))}
        <optgroup label="Escolher na produção">
          {categorias.map((c) => (
            <option key={c.id} value={`${NOVA_CATEGORIA}${c.id}`}>
              Qualquer {c.label}
            </option>
          ))}
        </optgroup>
      </select>

      <div className="space-y-1.5 rounded-lg bg-zinc-50 p-3 dark:bg-muted/40">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-600 dark:text-muted-foreground">Custo de insumos</span>
          <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
            {formatPriceCents(totais.totalCents)}
            {totais.temAberto && <span className="ml-1 text-[10px] font-normal text-amber-600">+ o que for escolhido</span>}
          </span>
        </div>
        {precoVenda != null && (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-600 dark:text-muted-foreground">Preço de venda</span>
              <span className="tabular-nums text-zinc-700 dark:text-foreground/80">{formatPriceCents(precoVenda)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-zinc-200 pt-1.5 text-xs dark:border-border">
              <span className="font-medium text-zinc-700 dark:text-foreground/80">Margem sobre insumos</span>
              <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
                {margem != null ? formatPriceCents(margem) : "—"}
              </span>
            </div>
            <p className="text-[10.5px] leading-snug text-zinc-500 dark:text-muted-foreground">
              Margem sobre <strong>insumos</strong>: não inclui bancada, energia nem estrutura.
            </p>
          </>
        )}
      </div>

      {totais.semPreco > 0 && (
        <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {totais.semPreco} {totais.semPreco === 1 ? "insumo sem preço de compra" : "insumos sem preço de compra"} — o
          custo está incompleto até cadastrar.
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        {sujo && <span className="mr-auto text-[11px] text-amber-600 dark:text-amber-400">Alterações não salvas</span>}
        <button
          type="button"
          onClick={novaFicha}
          disabled={fichaId === null && linhas.length === 0 && !nome}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-xs font-semibold text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
        >
          <FilePlus2 className="h-3.5 w-3.5" />
          Nova ficha
        </button>
        {fichaId && (
          <button
            type="button"
            onClick={duplicar}
            disabled={salvar.isPending}
            title="Criar outra ficha com as mesmas linhas e equipamentos"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-xs font-semibold text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
          >
            <Copy className="h-3.5 w-3.5" />
            Duplicar
          </button>
        )}
        {fichaId && (
          <button
            type="button"
            disabled={remover.isPending}
            onClick={() => {
              if (window.confirm(`Remover a ficha "${nome}"?`)) remover.mutate({ id: fichaId });
            }}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 px-2.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-950/20"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remover
          </button>
        )}
        <button
          type="button"
          disabled={salvar.isPending || !podeSalvar}
          onClick={() => salvar.mutate({ id: fichaId ?? undefined, ...corpoDaFicha(nome.trim()) })}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white disabled:opacity-50"
        >
          {salvar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {fichaId ? "Salvar ficha" : "Criar ficha"}
        </button>
      </div>

      {/* O insumo recém-criado já entra na receita: mandar a pessoa procurar na lista o que ela
          acabou de cadastrar é o tipo de passo que faz perder o que estava digitado. */}
      <MaterialLineDialog
        open={criandoInsumo}
        onClose={() => setCriandoInsumo(false)}
        onCriado={(m) => {
          setSujo(true);
          setLinhas((v) => [
            ...v,
            {
              materialLineId: m.id,
              materialCategoryId: null,
              rotulo: m.name,
              unidade: null,
              precoCents: m.purchasePriceCents,
              indireto: false,
              quantidade: "1",
              fator: "1",
            },
          ]);
        }}
      />
    </div>
  );
}
